// Master list of access-control "sub-items".
//
//   - Managed on the Sub-items page (the fixed master list).
//   - Assigned per-package on the Subscription page (CRUD), with a value.
//
// The server enforces these (1 Oct 2026, Backend/api/middleware/planControl.js);
// the clients only hide what the server would refuse. Copied byte-for-byte into
// Frontend-superadmin-web/src/data/accessControls.js — mirroredFiles.test.js
// fails on any difference.
//
//   type 'boolean' -> a package either includes it or not (toggle only)
//   type 'value'   -> a package assigns a value (e.g. patient limit = 20 / Unlimited)

// Sub-items are grouped under these two sections on the Sub-items page.
//   control = configured per subscription plan (toggle / value) AND refused by
//             the server when a doctor's plan lacks it (owner, 1 Oct 2026)
//   static  = bespoke enterprise deployment, drawn on the plan card only
export const SUB_ITEM_SECTIONS = [
  { id: 'control', label: 'Control features', description: 'Toggled or valued per subscription plan.' },
  { id: 'static', label: 'Static features', description: 'Delivered per bespoke deployment — shown on the plan card, not configured in the app.' },
];

// Groups the package editor draws the controls under (1 Oct 2026: the list
// grew from 15 to 23 entries once every plan feature became a real server
// gate, and one long column of ticks stopped being readable). `group` on each
// control below names one of these; the order here is the order on screen.
export const CONTROL_GROUPS = [
  { id: 'practice', label: 'Practice' },
  { id: 'consultation', label: 'Consultation' },
  { id: 'clinical', label: 'Clinical' },
  { id: 'ai', label: 'AI' },
  { id: 'visibility', label: 'Visibility' },
  { id: 'enterprise', label: 'Enterprise deployment' },
];

// Fixed support SLA tiers (response time), high → low. Used as the only options
// for the support_level control; no free-text. A ticket is routed to its tier.
export const SUPPORT_TIERS = ['Immediate', '12h', '24h', '48h'];

// 1 Oct 2026 (owner): the list is complete and every control is refused by the
// server (403 PLAN_FEATURE_MISSING) when a doctor's plan lacks it — except the
// four custom_* entries, which are a bespoke deployment and stay card-only.
// `dashboard` was removed (the dashboard is common to every doctor; the TENANT
// feature of the same name is a different switch and stays), and so was
// `patient_library` (the Patient Library page was removed earlier).
// Mirror of Backend/config/accessControls.js — mirroredFiles.test.js holds the
// two against each other.
export const ACCESS_CONTROLS = [
  // ---- Control features (per-plan) ----
  { id: 'patient_limit', label: 'Patient limit', type: 'value', section: 'control', group: 'practice', placeholder: 'e.g. 20, 50, Unlimited', description: 'Maximum number of patients a doctor can manage.' },
  { id: 'support_level', label: 'Support', type: 'value', section: 'control', group: 'practice', options: SUPPORT_TIERS, description: 'Support response-time SLA for the plan (Immediate / 12h / 24h / 48h).' },
  { id: 'email_reminders', label: 'Email reminders', type: 'boolean', section: 'control', group: 'practice', description: 'Automatic appointment reminders by email.' },
  { id: 'sms_reminders', label: 'SMS reminders', type: 'boolean', section: 'control', group: 'practice', description: 'Automatic appointment reminders by SMS.' },
  { id: 'secured_video', label: 'Secured video', type: 'boolean', section: 'control', group: 'consultation', description: 'Encrypted, HDS-compliant video consultations.' },
  { id: 'meeting_recording', label: 'Video recording', type: 'boolean', section: 'control', group: 'consultation', description: 'Record video consultations to the medical record.' },
  { id: 'doctor_patient_chat', label: 'Doctor–patient chat', type: 'boolean', section: 'control', group: 'consultation', description: 'In-app messaging between doctor and patient.' },
  { id: 'online_payment', label: 'Online payment features', type: 'boolean', section: 'control', group: 'consultation', description: 'Accept online card payments from patients.' },
  { id: 'custom_pricing', label: 'Custom consultation pricing', type: 'boolean', section: 'control', group: 'consultation', description: 'Set a custom consultation price per patient.' },
  { id: 'check_sets', label: 'Check sets & readings', type: 'boolean', section: 'control', group: 'clinical', description: 'The doctor\'s own check sets, and recording readings on their visits.' },
  { id: 'care_programmes', label: 'Care programmes', type: 'boolean', section: 'control', group: 'clinical', description: 'The doctor\'s own care programmes, and running them for patients.' },
  { id: 'visit_sharing', label: 'Shared visits & health record', type: 'boolean', section: 'control', group: 'clinical', description: 'Open the visits patients share from other providers, and their health record.' },
  { id: 'referrals', label: 'Refer & suggest', type: 'boolean', section: 'control', group: 'clinical', description: 'Refer patients to, or suggest, another doctor.' },
  { id: 'ai_appointment_summary', label: 'AI appointment summary', type: 'boolean', section: 'control', group: 'ai', description: 'AI-generated summary notes for each appointment.' },
  // Split in two (owner, 2 Oct 2026): the prescription audio notes keep the
  // old id; the transcript of a recorded video visit is its own control.
  { id: 'ai_transcription', label: 'Prescription audio transcription', type: 'boolean', section: 'control', group: 'ai', description: 'Transcribe the audio notes a doctor records on a visit\'s prescription.' },
  { id: 'ai_recording_transcription', label: 'Video visit transcription', type: 'boolean', section: 'control', group: 'ai', description: 'Transcript of a recorded video visit.' },
  { id: 'ai_ocr', label: 'AI · Notes OCR', type: 'boolean', section: 'control', group: 'ai', description: 'Upload handwritten notes and have the text read out.' },
  { id: 'ai_assistant', label: 'AI Assistant', type: 'boolean', section: 'control', group: 'ai', description: 'AI assistant / chatbot for the practice.' },
  { id: 'ai_reports', label: 'AI · Reports', type: 'boolean', section: 'control', group: 'ai', description: 'AI-generated analytics and reporting.' },
  { id: 'directory_listing', label: 'Platform directory listing', type: 'boolean', section: 'control', group: 'visibility', description: 'Be listed in the platform directory patients search.' },
  // ---- Static features (bespoke deployment, card only) ----
  { id: 'custom_website', label: 'Custom website', type: 'boolean', section: 'static', group: 'enterprise', description: 'Branded custom website for the practice.' },
  { id: 'custom_app', label: 'Custom app', type: 'boolean', section: 'static', group: 'enterprise', description: 'Branded custom mobile application.' },
  { id: 'custom_integrations', label: 'Custom integrations', type: 'boolean', section: 'static', group: 'enterprise', description: 'Bespoke third-party / system integrations.' },
  { id: 'custom_onboarding', label: 'Custom onboarding', type: 'boolean', section: 'static', group: 'enterprise', description: 'Dedicated onboarding & setup assistance.' },
];

// The controls added since 1 Oct 2026 (ten that day, plus
// ai_recording_transcription when transcription was split on 2 Oct). The
// default packages carry them on Elite and Custom only (Custom carries every
// boolean control); the rest are assigned in the package editor.
export const NEW_CONTROLS_2026_10 = [
  'check_sets', 'care_programmes', 'meeting_recording', 'visit_sharing',
  'ai_transcription', 'ai_recording_transcription', 'ai_ocr', 'ai_reports',
  'directory_listing', 'custom_pricing', 'referrals',
];
const ALL_NEW = Object.fromEntries(NEW_CONTROLS_2026_10.map((id) => [id, true]));

// Card accent themes for packages. Full class strings live here so Tailwind's JIT
// detects them (no dynamic concatenation of color names).
export const PACKAGE_ACCENTS = {
  slate: { label: 'Slate', bar: 'from-slate-400 to-slate-500', text: 'text-slate-700', badge: 'from-slate-500 to-slate-600', soft: 'bg-slate-50', dot: 'bg-slate-400' },
  blue: { label: 'Blue', bar: 'from-blue-400 to-blue-500', text: 'text-blue-600', badge: 'from-blue-500 to-blue-600', soft: 'bg-blue-50', dot: 'bg-blue-400' },
  emerald: { label: 'Emerald', bar: 'from-emerald-400 to-teal-500', text: 'text-emerald-600', badge: 'from-emerald-500 to-teal-600', soft: 'bg-emerald-50', dot: 'bg-emerald-500' },
  amber: { label: 'Amber', bar: 'from-amber-400 to-amber-500', text: 'text-amber-600', badge: 'from-amber-500 to-amber-600', soft: 'bg-amber-50', dot: 'bg-amber-400' },
  orange: { label: 'Orange', bar: 'from-[#e07a2f] to-amber-500', text: 'text-[#b45309]', badge: 'from-[#e07a2f] to-amber-600', soft: 'bg-orange-50', dot: 'bg-[#e07a2f]' },
  teal: { label: 'Teal', bar: 'from-[#4ABDBD] to-[#2D8A8A]', text: 'text-[#3AA8A8]', badge: 'from-[#4ABDBD] to-[#2D8A8A]', soft: 'bg-[#4ABDBD]/10', dot: 'bg-[#4ABDBD]' },
  rose: { label: 'Rose', bar: 'from-rose-400 to-pink-500', text: 'text-rose-600', badge: 'from-rose-500 to-pink-600', soft: 'bg-rose-50', dot: 'bg-rose-400' },
  violet: { label: 'Violet', bar: 'from-violet-500 to-purple-600', text: 'text-violet-600', badge: 'from-violet-500 to-purple-600', soft: 'bg-violet-50', dot: 'bg-violet-500' },
  cyan: { label: 'Cyan', bar: 'from-cyan-400 to-sky-500', text: 'text-cyan-600', badge: 'from-cyan-500 to-sky-600', soft: 'bg-cyan-50', dot: 'bg-cyan-400' },
  indigo: { label: 'Indigo', bar: 'from-indigo-500 to-blue-600', text: 'text-indigo-600', badge: 'from-indigo-500 to-blue-600', soft: 'bg-indigo-50', dot: 'bg-indigo-500' },
};
export const ACCENT_KEYS = Object.keys(PACKAGE_ACCENTS);

// Card layout templates a package can use (chosen in the CRUD form).
//   bar     -> white card + accent top bar (default)
//   header  -> accent-filled header band (name + price in white)
//   dark    -> dark card with accent text
//   outline -> minimal white card with a thick accent border
export const CARD_STYLES = [
  { id: 'bar', label: 'Accent bar' },
  { id: 'header', label: 'Filled header' },
  { id: 'dark', label: 'Dark' },
  { id: 'outline', label: 'Outline' },
];
export const STYLE_KEYS = CARD_STYLES.map((s) => s.id);

// Billing periods a paid package can offer. `days` drives the subscription
// period length; admin enables the ones they want + sets a price per period.
// Mirror of the backend BILLING_PERIODS in config/accessControls.js.
export const BILLING_PERIODS = [
  { id: 'monthly', label: 'Monthly', days: 30 },
  { id: 'quarterly', label: 'Quarterly', days: 90 },
  { id: 'halfyearly', label: 'Half-year', days: 180 },
  { id: 'yearly', label: 'Yearly', days: 365 },
];

// Seed packages — mirror of Backend/config/accessControls.js DEFAULT_PACKAGES,
// used by the package editor when the API list is still empty. `controls[id]`
// is `true` for a boolean control or a string for a value control;
// absent/false means "not included in this package".
//
// PRICES ARE IN RUPEES (INR), and they are FRESH INSTALLS ONLY — an existing
// deployment re-prices via the admin UI (Subscription -> package editor), so
// changing a number here never moves a price a customer is already on.
// The longer periods are priced off the monthly rate: quarterly 3x,
// half-year 6x, yearly 10x (two months free).
export const DEFAULT_PACKAGES = [
  {
    id: 'free', name: 'Free', price: '0', accent: 'slate', style: 'bar', popular: false, badgeText: '', durationDays: 14,
    controls: {
      patient_limit: '10',
      email_reminders: true, support_level: '48h',
    },
  },
  {
    id: 'basic', name: 'Basic', price: '999', accent: 'blue', style: 'bar', popular: false, badgeText: '', durationDays: 30,
    controls: {
      patient_limit: '50',
      email_reminders: true, support_level: '48h', secured_video: true, online_payment: true,
    },
    billingPeriods: {
      monthly: { enabled: true, price: '999' }, quarterly: { enabled: true, price: '2997' },
      halfyearly: { enabled: true, price: '5994' }, yearly: { enabled: true, price: '9990' },
    },
  },
  {
    id: 'premium', name: 'Premium', price: '1999', accent: 'emerald', style: 'header', popular: true, badgeText: 'Most Popular', durationDays: 30,
    controls: {
      patient_limit: '200',
      email_reminders: true, sms_reminders: true, support_level: '24h',
      secured_video: true, doctor_patient_chat: true,
      online_payment: true,
    },
    billingPeriods: {
      monthly: { enabled: true, price: '1999' }, quarterly: { enabled: true, price: '5997' },
      halfyearly: { enabled: true, price: '11994' }, yearly: { enabled: true, price: '19990' },
    },
  },
  {
    id: 'elite', name: 'Elite', price: '3999', accent: 'violet', style: 'dark', popular: false, badgeText: '', durationDays: 30,
    controls: {
      patient_limit: 'Unlimited',
      email_reminders: true, sms_reminders: true, support_level: '12h',
      secured_video: true, doctor_patient_chat: true,
      online_payment: true, ai_appointment_summary: true, ai_assistant: true,
      ...ALL_NEW,
    },
    billingPeriods: {
      monthly: { enabled: true, price: '3999' }, quarterly: { enabled: true, price: '11997' },
      halfyearly: { enabled: true, price: '23994' }, yearly: { enabled: true, price: '39990' },
    },
  },
  {
    // Contact-only: it has no price, so it is arranged with the team through
    // /api/contact-inquiry rather than requested and invoiced like the others.
    id: 'custom', name: 'Custom', price: '', purchasable: false, accent: 'orange', style: 'outline', popular: false, badgeText: '', durationDays: 30,
    // Custom is "everything, arranged with the team" (1 Oct 2026, as the
    // backend): every boolean control, the reminders included.
    controls: {
      patient_limit: 'Unlimited', support_level: 'Immediate',
      ...Object.fromEntries(ACCESS_CONTROLS.filter((c) => c.type === 'boolean').map((c) => [c.id, true])),
    },
  },
];

// Whether a package includes a given control (truthy value or boolean true).
export const isControlIncluded = (controls, id) =>
  controls?.[id] !== undefined && controls?.[id] !== false && controls?.[id] !== '';

let _seq = 0;
// Local-only id generator for new packages (replaced by backend ids later).
export const nextPackageId = () => `pkg_${Date.now()}_${_seq++}`;
